import { Link } from '@tanstack/react-router';
import { ArrowLeft, Bot, DoorOpen, CircleCheck, CircleX, FileText, Gavel, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  canRecordWithdrawal,
  criterionTagLine,
  type ApplicationDetailResponse,
} from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { ModuleGate } from '../../../components/ModuleGate';
import { Select } from '../../../components/Select';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { RESULT_TONES } from '../../../components/status-tones';
import { toneClass } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanEdit } from '../../../stores/session';
import { useSourceOptions } from '../../../lib/app-settings';
import { useToasts } from '../../../stores/toasts';
import { Button } from '../../../components/Button';
import { Popover, usePopover } from '../../../components/Popover';
import { WithdrawalPicker } from '../components/WithdrawalPicker';
import { ApplicationTestsCard } from '../../assessments/components/ApplicationTestsCard';
import { ApplicationInterviewsCard } from '../../interviews/components/ApplicationInterviewsCard';
import { documentUrl, useApplication, useCorrectSource } from '../api/candidates-api';
import { fileSize } from '../candidate-format';
import { CrossApplicationBanner, StageChip } from '../components/CandidateBits';
import { ProfileBreakdownView } from '../../ranking/components/ProfileBreakdownView';
import profile from './CandidateProfile.module.css';
import styles from './ApplicationPage.module.css';
import { Timeline } from '../../communication/components/Timeline';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';

const PERCENT = 100;

/**
 * CAND-03 — the operational record of one application: what they answered, the §L1
 * score and why, the knockout results, where they came from, their documents and
 * every move so far. Decisions arrive with S21.
 */
export function ApplicationPage({ applicationId }: { readonly applicationId: string }): ReactNode {
  const detail = useApplication(applicationId);
  useRecordVisit(detail.data ? { kind: 'application', id: applicationId, title: detail.data.application.candidate.fullName, subtitle: `${detail.data.application.jobTitle} · ${detail.data.application.reference}`, path: `/applications/${applicationId}` } : null, goneId(detail.error, applicationId));
  const canEdit = useCanEdit('application');
  const isPoolsOn = useModule('pools');

  if (detail.isPending) return <DetailSkeleton />;
  if (detail.error) {
    if (detail.error.status === 403) return <ForbiddenState resource="this application" contact="Your admin" />;
    if (detail.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void detail.refetch()} />;
  }
  const data = detail.data;
  const { application } = data;
  const candidate = application.candidate;

  return (
    <div className={profile.page}>
      <header className={profile.header}>
        <Link to="/candidates/$candidateId" params={{ candidateId: candidate.id }} className={profile.back}>
          <ArrowLeft size={14} aria-hidden />
          {candidate.fullName}
        </Link>
        <div className={profile.titleRow}>
          <div className={profile.titleBlock}>
            <div className={profile.titleLine}>
              <h1 className={profile.title}>{application.jobTitle}</h1>
              <StageChip stage={application.stage} />
              {candidate.doNotRehire && <Chip tone="danger">Do not rehire</Chip>}
            </div>
            <p className={profile.meta}>
              {[
                candidate.fullName,
                application.reference,
                application.ventureName,
                `Applied ${formatDate(application.appliedAt)}`,
              ].join(' · ')}
            </p>
          </div>
          <span className={styles.headActions}>
            {canEdit && canRecordWithdrawal(application.stage) && <RecordWithdrawalButton applicationId={application.id} version={application.version} candidateName={candidate.fullName} />}
            <Link to="/jobs/$jobId" params={{ jobId: application.jobId }} className={styles.jobLink}>
              {application.jobReference}
            </Link>
          </span>
        </div>
      </header>

      <CrossApplicationBanner applications={data.liveElsewhere} lead="Also in progress elsewhere" />
      {application.rejectionReasonCode && (
        <p className={profile.notice}>
          <strong>Closed:</strong> {application.rejectionReasonLabel ?? application.rejectionReasonCode}
          {application.rejectionNote ? ` — ${application.rejectionNote}` : ''}
        </p>
      )}

      <div className={profile.grid}>
        <div className={profile.main}>
          <Card title="Match" meta="A sorting aid, never a decision">
            <ScoreSection data={data} />
          </Card>
          <Card title="Answers" meta="As given on this application">
            <dl className={profile.facts}>
              {application.formAnswers.map((row) => (
                <div key={row.label} className={profile.fact}>
                  <dt>{row.label}</dt>
                  <dd>{row.isRestricted ? <span title="Not visible to your role">—</span> : (row.value ?? '—')}</dd>
                </div>
              ))}
            </dl>
          </Card>
          {application.questionAnswers.length > 0 && (
            <Card title="Questions">
              <ul className={styles.questions}>
                {application.questionAnswers.map((row) => (
                  <li key={row.questionId} className={styles.question}>
                    <div>
                      <p className={styles.questionText}>{row.question}</p>
                      <p className={profile.small}>{row.answer ?? '—'}</p>
                    </div>
                    {row.isKnockout && row.didPass !== null && (
                      <span className={`${toneClass} ${styles.knockout}`} data-tone={row.didPass ? RESULT_TONES.passed : RESULT_TONES.below}>
                        {row.didPass ? <CircleCheck size={14} aria-hidden /> : <CircleX size={14} aria-hidden />}
                        {row.didPass ? 'Passed' : knockoutLabel(row.knockoutAction)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Timeline subject={{ kind: 'application', id: applicationId }} />
          <Card title="History">
            <ol className={styles.history}>
              {data.history.map((row) => (
                <li key={row.id} className={styles.historyRow}>
                  <span className={styles.historyWhen}>{formatDateTime(row.occurredAt)}</span>
                  <span className={styles.historyWhat}>
                    {row.summary}
                    <span className={profile.quiet}>
                      {row.isSystem ? (
                        <span className={styles.automatic}>
                          <Bot size={12} aria-hidden /> Automatic
                        </span>
                      ) : (
                        (row.actorName ?? '')
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <div className={profile.side}>
          <ApplicationInterviewsCard applicationId={application.id} />
          <ModuleGate module="assessments">
            <ApplicationTestsCard applicationId={application.id} jobId={application.jobId} candidateName={candidate.fullName} stage={application.stage} version={application.version} />
          </ModuleGate>
          <Card title="Source">
            <SourceSection data={data} />
          </Card>
          <Card title="Documents">
            {data.documents.length === 0 ? (
              <p className={profile.quiet}>No documents on this application.</p>
            ) : (
              <ul className={profile.plainList}>
                {data.documents.map((document) => (
                  <li key={document.id} className={profile.document}>
                    <FileText size={16} aria-hidden className={profile.documentIcon} />
                    {document.isDeleted ? (
                      <span className={profile.quiet}>{document.fileName} — removed</span>
                    ) : (
                      <a href={documentUrl(document.id)} target="_blank" rel="noreferrer">
                        {document.fileName}
                      </a>
                    )}
                    <span className={profile.quiet}>
                      {document.label} · {fileSize(document.byteSize)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Decisions">
            <SystemState
              icon={Gavel}
              title="Screening decisions are made in sorting mode"
              body={`${isPoolsOn ? 'Advance, reject and pool' : 'Advance and reject'} — each with its reason — happen in sorting mode from the job. Round decisions come with the interview debriefs.`}
              isInset
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

/** O11: "why was this candidate placed here?" answered on the record itself — by the ideal candidate's lines (ADR 0062), or the criteria's table for an application still ranked by them. */
function ScoreSection({ data }: { readonly data: ApplicationDetailResponse }): ReactNode {
  const { application } = data;
  if (application.profileBreakdown) return <ProfileBreakdownView breakdown={application.profileBreakdown} />;
  if (application.matchScore === null) return <p className={profile.quiet}>Not ranked yet.</p>;
  return (
    <div className={styles.score}>
      <div className={styles.scoreHead}>
        <span className={styles.scoreValue}>{application.matchScore}</span>
        <span className={profile.small}>
          out of 100
          {application.rawMatchScore !== null && application.rawMatchScore !== application.matchScore
            ? ` · ${application.rawMatchScore} before the cap and penalties`
            : ''}
        </span>
      </div>
      {application.isMissingMustHave && (
        <p className={styles.capped}>
          <TriangleAlert size={14} aria-hidden />
          Missing must-have: {application.missingMustHaves.join(', ')}. The score cannot go above 49.
        </p>
      )}
      {application.knockoutPenalty > 0 && <p className={profile.small}>Knockout penalty: −{application.knockoutPenalty} points.</p>}
      {application.isKnockoutFlagged && <p className={profile.small}>A knockout question asked for a closer look.</p>}
      <div className={styles.tableWrap}>
        <table className={styles.breakdown}>
          <thead>
            <tr>
              <th scope="col">Criterion</th>
              <th scope="col">Evidence</th>
              <th scope="col" className={styles.numeric}>
                Match
              </th>
              <th scope="col" className={styles.numeric}>
                Points
              </th>
            </tr>
          </thead>
          <tbody>
            {application.breakdown.map((item) => (
              <tr key={item.criterionId}>
                <td>
                  <span className={styles.criterion}>{item.label}</span>
                  <span className={profile.quiet}>
                    {criterionTagLine(item)} · weight {item.weight}
                  </span>
                </td>
                <td>{item.evidence}</td>
                <td className={styles.numeric}>{Math.round(item.match * PERCENT)}%</td>
                <td className={styles.numeric}>{item.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** S14 Q7: a "direct" application named by a recruiter, in one pick. */
function SourceSection({ data }: { readonly data: ApplicationDetailResponse }): ReactNode {
  const { application } = data;
  const correct = useCorrectSource(application.id);
  const show = useToasts((state) => state.show);
  const sourceOptions = useSourceOptions('record');

  async function pick(value: string): Promise<void> {
    const option = sourceOptions.find((candidate) => candidate.value === value);
    if (!option) return;
    try {
      await correct.mutateAsync({ source: option.value, version: application.version, label: option.label });
      show({ tone: 'success', message: `Source set to ${option.label}` });
    } catch (cause) {
      show({ tone: 'danger', message: "The source wasn't changed", detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.source}>
      <p className={styles.sourceLabel}>
        {application.sourceLabel}
        {application.needsSourceReview && (
          <Chip tone="warning" plain>
            Needs review
          </Chip>
        )}
      </p>
      {application.referrerName && <p className={profile.small}>Referred by {application.referrerName}</p>}
      {data.can.correctSource && (
        <Field label="Where did this application really come from?" hint="It arrived without a tracked link, so it counts as direct until someone names the channel.">
          {({ inputId, describedBy }) => (
            <Select
              id={inputId}
              describedBy={describedBy}
              label="Where did this application really come from?"
              placeholder="Choose a source"
              value={null}
              options={sourceOptions}
              onChange={(value) => void pick(value)}
            />
          )}
        </Field>
      )}
    </div>
  );
}

function knockoutLabel(action: string): string {
  if (action === 'auto_reject') return 'Failed — not selected';
  if (action === 'score_penalty') return 'Failed — points taken off';
  return 'Failed — flagged';
}

function DetailSkeleton(): ReactNode {
  return (
    <div className={profile.page}>
      <Skeleton shape="title" width="40%" />
      <SkeletonLines count={2} />
      <SkeletonLines count={8} />
    </div>
  );
}

/** S29 Q4 — the picker opens beside the button (CLAUDE.md §5). */
function RecordWithdrawalButton({ applicationId, version, candidateName }: { readonly applicationId: string; readonly version: number; readonly candidateName: string }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={DoorOpen} onClick={toggle} aria-haspopup="dialog">
        Record withdrawal
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" label={`Record that ${candidateName} withdrew`}>
        <WithdrawalPicker applicationId={applicationId} version={version} candidateName={candidateName} onDone={close} />
      </Popover>
    </>
  );
}
